import { Component, DestroyRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { API, ClientDTO, UserClientFullDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UtilService } from 'src/app/services/util.service';
import { SbAccessibleClientsComponent } from '../../../components/sb-accessible-clients/sb-accessible-clients.component';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-profile-overview',
  templateUrl: './user-profile-overview.component.html',
  styleUrls: ['./user-profile-overview.component.scss'],
  standalone: false,
})
export class UserProfileOverviewComponent implements OnInit {
  loading: boolean;
  user: UserClientFullDTO;
  primaryClient: ClientDTO;
  primaryClientTouched: boolean = false;
  form = new UntypedFormGroup({
    FirstName: new UntypedFormControl('', [Validators.required]),
    LastName: new UntypedFormControl('', [Validators.required]),
    Email: new UntypedFormControl('', [Validators.required]),
    UserName: new UntypedFormControl('', [Validators.required]),
  });
  firstNameFallback: string;
  lastNameFallback: string;
  userNameFallback: string;
  emailFallback: string;
  primaryClientFallback: ClientDTO;

  constructor(
    private api: API,
    private modalService: ModalService,
    public util: UtilService,
    public translate: TranslateService,
    public errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {}

  get formInvalid(): boolean {
    return (
      !(this.primaryClientTouched || this.FirstName?.touched || this.LastName?.touched || this.UserName.touched) &&
      (this.FirstName.errors?.required || this.LastName.errors?.required || this.Email.errors?.required || this.UserName.errors?.required)
    );
  }

  get FirstName() {
    return this.form.get('FirstName');
  }

  get LastName() {
    return this.form.get('LastName');
  }

  get UserName() {
    return this.form.get('UserName');
  }

  get Email() {
    return this.form.get('Email');
  }

  ngOnInit(): void {
    this.refreshFromServer();
    // this.UserName.disable();
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .shared_Status_status()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: UserClientFullDTO) => {
          this.user = data;
          this.refreshFormFromDTO();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  refreshFormFromDTO() {
    this.FirstName.setValue(this.user.user.firstname);
    this.LastName.setValue(this.user.user.lastname);
    this.UserName.setValue(this.user.user.username);
    this.Email.setValue(this.user.user.email);
    this.primaryClient = this.user.user.primaryClient;

    this.firstNameFallback = this.user.user.firstname;
    this.lastNameFallback = this.user.user.lastname;
    this.userNameFallback = this.user.user.username;
    this.emailFallback = this.user.user.email;
    this.primaryClientFallback = this.user.user.primaryClient;
  }

  refreshDTOFromForm() {
    this.user.user.firstname = this.FirstName.value;
    this.user.user.lastname = this.LastName.value;
    this.user.user.username = this.UserName.value;
    this.user.user.email = this.Email.value;
  }

  resetForm() {
    this.FirstName.reset();
    this.LastName.reset();
    this.UserName.reset();
    this.Email.reset();
    this.primaryClientTouched = false;
  }

  clientModal() {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SbAccessibleClientsComponent, data: { connectedClients: false } },
        title: null,
        modalOptions: {
          // windowClass: 'bigXL',
          size: 'xl',
        },
      })
      .then((result: ClientDTO) => {
        if (result?.id) {
          this.primaryClient = result;
          this.primaryClientTouched = true;
        }
      })
      .catch(() => {});
  }

  submit() {
    const observable = [];

    const touched: boolean =
      this.primaryClientTouched || this.FirstName.touched || this.LastName.touched || this.Email.touched || this.UserName.touched;

    if (touched) {
      const oldUser = { ...this.user.user };
      this.refreshDTOFromForm();
      this.loading = true;

      if (this.primaryClientTouched) {
        observable.push(this.api.shared_User_changePrimaryClient(this.primaryClient));
      }
      if (
        (this.FirstName.touched && this.user.user.firstname !== oldUser.firstname) ||
        (this.LastName.touched && this.user.user.lastname !== oldUser.lastname)
      ) {
        observable.push(this.api.shared_User_edit(this.user.user));
      }
      if (this.UserName.touched && this.user.user.email !== oldUser.username) {
        observable.push(this.api.shared_User_changeUsername({ newUsername: this.user.user.username }));
      }
      if (this.Email.touched && this.user.user.email !== oldUser.email) {
        observable.push(this.api.shared_User_changeEmail({ newEmail: this.user.user.email }));
      }

      forkJoin(observable)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.resetForm();
            this.refreshFromServer();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }
}
